import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PlanTable from "../../components/ui/planTable/PlanTable";
import Footer from "../../components/Footer.js/Footer";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Host.css";

const Host = () => {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggleState, setToggleState] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://api.spadserver.com/api/product/${name}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setData(result.data);
        setError(null);
        document.title = result.data?.meta_title || "Default Title";
      } catch (error) {
        setError(`Error fetching data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [name]);

  if (loading) return <LoadingSpinner />;
  if (error)
    return (
      <div className="flex justify-center">
        <img src="/images/404.png" alt="Error" />
      </div>
    );
  if (!data) return <div>No data available</div>;

  const uniqueCategories = Array.from(
    new Map(
      data.products
        .filter(
          (product) =>
            product.service_category_id && product.service_category_name
        )
        .map((product) => [
          product.service_category_id,
          product.service_category_name,
        ])
    ).entries()
  );

  const handleToggleSwitch = (index) => {
    setToggleState(index);
  };

  const currentCategoryId =
    uniqueCategories.length > 0 ? uniqueCategories[toggleState][0] : null;

  const filteredProducts = currentCategoryId
    ? data.products.filter(
        (product) => product.service_category_id === currentCategoryId
      )
    : data.products;

  const uniqueProducts = filteredProducts.filter(
    (product, index, self) =>
      index === self.findIndex((p) => p.title === product.title)
  );

  // Reverse products for mobile view
  const displayedProducts = isMobile
    ? [...uniqueProducts].reverse()
    : uniqueProducts;

  // Calculate slidesToShow and slidesToScroll
  const slidesToShow = Math.min(displayedProducts.length, 4);
  const slidesToScroll = Math.min(1, displayedProducts.length - 1);

  // Settings for the carousel
  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    arrows: true,
    responsive: [
      {
        breakpoint: 1290,
        settings: {
          slidesToShow: Math.min(displayedProducts.length, 2),
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: Math.min(displayedProducts.length, 1),
          slidesToScroll: 1,
          arrows: true,
          rtl: true, // Enable right-to-left for mobile
        },
      },
      {
        breakpoint: 480,
        settings: {
          infinite: displayedProducts.length > 1,
          speed: 500,
          slidesToShow: Math.min(displayedProducts.length, 1),
          slidesToScroll: 1,
          arrows: true,
          rtl: true, // Enable right-to-left for smaller screens
        },
      },
    ],
  };

  return (
    <div>
      <div className="container mx-auto">
        <div
          className="grid lg:grid-cols-2 md:grid-cols-1 gap-3 mx-auto"
          style={{ width: "100%" }}
        >
          <div className="text-right">
            <p className="lg:text-[26px] sm:text-[12px] sm:font-bold text-white">
              {data.title}
            </p>
            <div
              className="lg:mt-24 mt-4 mb-6 text-white text-justify text-[13px] lg:text-[15px]"
              dangerouslySetInnerHTML={{ __html: data.description }}
            ></div>
          </div>
          <div className="lg:text-left md:text-center xl:text-left sm:text-center">
            <img
              src={`https://admin.spadserver.com/storage/${data.photo.path}`}
              alt="Host"
              className="w-full max-w-md h-auto inline-block"
            />
          </div>
        </div>
      </div>

      <div className="planName mt-16">
        <p className="text-white lg:text-[26px] md:text-[20px] sm:text-[16px] text-center">
          {data.meta_title}
        </p>

        {uniqueCategories.length > 0 && (
          <div className="flex justify-center mt-14">
            <div className="bg-switch-full rounded-full p-2 flex space-x-1">
              {uniqueCategories.map((category, index) => (
                <button
                  key={category[0]}
                  className={`rounded-full px-4 py-2 w-[6rem] text-sm font-medium transition ${
                    toggleState === index
                      ? "bg-switch text-black"
                      : "bg-switch-off text-white"
                  }`}
                  onClick={() => handleToggleSwitch(index)}
                >
                  {category[1]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="wrapper-swipp lg:p-[75px]">
          {displayedProducts.length > 1 ? (
            <Slider {...settings}>
              {displayedProducts.reverse().map((product, index) => (
                <div key={index} className="md:p-4 md:px-0 md:mx-0 p-12 mx-0">
                  <div className="plan-table-wrapper">
                    <PlanTable products={[product]} />
                  </div>
                </div>
              ))}
            </Slider>
          ) : (
            <div className="flex justify-center p-4">
              <div className="plan-table-wrapper">
                <PlanTable products={displayedProducts} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Host;
